import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is internal ticketing software?',
    a: 'Internal ticketing software lets a company\u2019s own people send requests, such as IT help, facilities, finance or ops tasks, to the team that handles them, and tracks each request from open to resolved. It replaces requests scattered across chat and email.',
  },
  {
    q: 'Can Kora be used as an IT helpdesk for a small team?',
    a: 'Yes. Kora has claim-based and assignment-based workflows, categories you manage, priorities from Low to Critical, SLA tracking, automation rules and a public API. Members are unlimited and free, so every employee who handles requests can have a login.',
  },
  {
    q: 'How do employees submit requests?',
    a: 'Through the dashboard, the embeddable support form on your intranet or site, the public API, or the customer portal where people sign in with an emailed link. Email-to-ticket and a Slack integration are not available yet; they are on the roadmap.',
  },
  {
    q: 'How does pricing work for an internal team?',
    a: 'There are no seats and no monthly plan. Each ticket created costs 1 credit, which is $0.10, and $1 buys 10 credits. A team that handles 300 requests a month spends about $30. New workspaces start with 1,100 free credits.',
  },
  {
    q: 'Can I control who sees and does what?',
    a: 'Yes. Kora has six roles: Owner, Admin, Manager, Operations, Agent and Viewer. Viewers get read-only access for stakeholders, and agents work the tickets assigned to them.',
  },
  {
    q: 'Can I set response targets for internal requests?',
    a: 'Yes. SLA policies set first-response and resolution targets per priority and can be scoped by category. Clocks follow your business hours, can pause while you wait on the requester, and a breach alerts the assignee and owners. SLA tracking is free.',
  },
  {
    q: 'Can I automate routine requests?',
    a: 'Yes. Automation rules react to ticket events and timers: assign, set priority or status, add a comment, email a member or fire a webhook. A dry-run shows what a rule would do on your last 20 tickets before you switch it on. Automation is free.',
  },
  {
    q: 'Where is the data stored?',
    a: 'Ticket data lives in Cloudflare D1 in the Europe region, and uploaded files are served through expiring signed links.',
  },
];

export function InternalTicketingSoftwarePage() {
  useEffect(() => {
    const title = 'Internal Ticketing Software for IT and Ops Teams | Kora';
    const description = 'Internal ticketing for IT, ops and engineering teams: categories, SLAs, automation and an API. Unlimited members, $0.10 per ticket, free credits to start.';
    setDocumentMeta(title, description);
    setCanonical('/internal-ticketing-software');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/internal-ticketing-software', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Internal ticketing software', url: 'https://usekora.app/internal-ticketing-software' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Use-case guide"
      title="Internal ticketing software for IT and ops teams"
      subtitle="Give your own people one place to ask for help, and your team one queue to work from, without paying per seat."
      answer={
        <>
          <strong>Kora works as internal ticketing software for IT, ops and engineering teams.</strong> Requests become tickets
          with a category, a priority and an owner, SLA clocks track your targets, and automation handles the routine. Members
          are unlimited and free, and each ticket costs $0.10. Email-to-ticket and Slack are not available yet.
        </>
      }
      ctaTitle="Set up your internal queue in minutes"
      ctaSubtitle="1,100 free credits, no card, no seats."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mt-4 mb-3">
        What an internal ticketing system should do
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-4">
        Internal requests usually arrive as chat messages, hallway questions and forwarded emails. An internal ticketing system
        gives each one a record: who asked, what they need, who owns it and when it is due. The team stops searching threads,
        and managers can see load and response times instead of guessing.
      </p>
      <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300 mb-6 list-disc pl-5">
        <li>One queue with categories such as Access, Hardware, Software and Facilities, managed by you.</li>
        <li>Priorities from Low to Critical and a clear status flow from Open to Resolved to Closed.</li>
        <li>Ownership: claim-based when the team swarms a queue, assignment-based when requests have named owners.</li>
        <li>Targets and alerts so important requests do not sit.</li>
        <li>Rules that route and escalate without manual triage.</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        How Kora covers it
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300 mb-6 list-disc pl-5">
        <li><strong>Paste to ticket:</strong> paste a chat message or email and AI proposes a title, category and priority. It is free.</li>
        <li><strong>SLA tracking:</strong> first-response and resolution targets per priority and category, business hours, pause while waiting, breach alerts. Free.</li>
        <li><strong>Automation:</strong> assign, set priority or status, comment, email a member or fire a webhook, with a dry-run first. Free.</li>
        <li><strong>Six roles:</strong> Owner, Admin, Manager, Operations, Agent and Viewer.</li>
        <li><strong>API and webhooks:</strong> create tickets from your own tools and send events out, with signed webhooks.</li>
        <li><strong>Employee-facing portal:</strong> people sign in with an emailed link and see only their own requests.</li>
        <li><strong>AI Analyst:</strong> ask plain-English questions about your ticket data (1 credit per answer).</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        What it costs for an internal team
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-6">
        A team handling 300 requests a month spends 300 credits, which is $30, however many people are on the team. Seats, comments,
        SLA tracking and automation add nothing. See the{' '}
        <Link to="/helpdesk-cost-calculator" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">cost calculator</Link>{' '}
        to compare against a per-seat tool at your own volume.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        What is not available yet
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-6">
        Email-to-ticket, a Slack integration and live chat are planned and not available today, so requests enter through the
        dashboard, the support form, the API or the portal. See the{' '}
        <Link to="/roadmap" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">roadmap</Link>. If your team needs a shared inbox
        or chat intake now, choose a tool that has it.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 dark:divide-zinc-800 border-y border-zinc-100 dark:border-zinc-800 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 dark:border-blue-500/20 bg-blue-50/50 dark:bg-blue-500/5 p-5">
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
          Related: <Link to="/sla-tracking-software" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">SLA tracking</Link>,{' '}
          <Link to="/helpdesk-automation" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">automation</Link>,{' '}
          <Link to="/customer-portal-software" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">customer portal</Link>,{' '}
          <Link to="/pricing" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">pricing</Link>.
        </p>
      </div>
    </ContentPage>
  );
}
